import React from 'react';
import HubPageLayout from '../components/HubPageLayout';
import ScreeningTiles from './ScreeningTiles';

// MoSAIC PSYOPS Explained — the reading companion to the two screenings.
//
// Deliberately a straight scroll: no tabs, no panels, no images. Someone who
// lands here is trying to understand what was done to them, and every extra
// control is one more thing between them and the sentence that names it.
//
// Layout per the mockup: section title in the left column, body in the right,
// hairline rule between sections.

const SECTIONS = [
  {
    title: 'MoSAIC PSYOPS Explained',
    body: [
      'MoSAIC PSYOPS is the behavioural side of No / SCAM. The rest of the Information Hub describes scams from the outside — what they look like, what they cost, which platforms carry them. MoSAIC works from the inside, on the psychological operation being run on a single person.',
      'That distinction matters because the people running these scams are not improvising. They work from a script with stages, and each stage has a purpose. Naming the stage you are standing in is what breaks it.',
    ],
  },
  {
    title: 'Psychological Manipulation 101 ( MoSAIC Explained, #1 )',
    body: [
      'Techniques range from exploiting emotional vulnerabilities, manipulating emotions, and influencing decision-making processes to achieve the manipulator’s goals.',
      'Manipulation is a process, not a single event. It opens with target selection — a profile that signals loneliness, recent loss, retirement, or money. It moves to rapport, where the manipulator mirrors your values back to you until being understood feels like being found. Then isolation: your doubts get reframed as other people not understanding what the two of you have.',
      'By the time money is discussed, the groundwork is finished. The request never arrives cold — it arrives as a favour between people who trust each other. That is the whole point of the preceding weeks.',
    ],
  },
  {
    title: 'Attack Vectors ( MoSAIC Explained, #2 )',
    body: [
      'Social platforms and dating services are the hunting grounds. Facebook, Instagram, LinkedIn, Silver Singles, Our Time, eHarmony and Match.com are effectively their own internet, and every user profile is a small website advertising what somebody wants and what they have lost.',
      'The opening move is almost always unsolicited: an out-of-the-blue text, a wrong-number message, a friend request from a stranger with a thin photo history, a comment on a post about a death in the family. None of it looks like an attack, because an attack that looked like one would not work.',
      'The vector is chosen to fit the target. A recent widow and a mid-career professional get approached differently, on different platforms, with different bait — but the stages that follow are the same script.',
    ],
  },
  {
    title: 'Challenges & Miscomputation ( MoSAIC Explained, #3 )',
    body: [
      'The hardest part of this scam is that the person inside it is the last to see it. That is not a failure of intelligence. Judgement is computed from the information you have, and the manipulator has spent weeks controlling exactly that.',
      'Three things push the calculation off. Emotional investment makes disconfirming evidence feel like disloyalty. Sunk cost makes walking away feel like admitting the whole relationship was nothing. And shame makes the one thing that would end it — describing the situation out loud to somebody else — the hardest thing to do.',
      'The screenings exist for that reason. They ask about behaviour rather than feelings, because behaviour is the part a manipulator cannot dress up, and because answering a question is easier than volunteering a confession.',
    ],
  },
];

const Section = ({ title, body, first }) => (
  <div className={first ? 'py-10' : 'py-10 border-t border-gray-300'}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
      <h2 className="md:col-span-5 text-2xl xl:text-3xl font-bold text-black leading-tight mb-0">
        {title}
      </h2>
      <div className="md:col-span-7">
        {body.map((para) => (
          <p key={para.slice(0, 40)} className="text-black text-base leading-relaxed mb-4 last:mb-0">
            {para}
          </p>
        ))}
      </div>
    </div>
  </div>
);

// Straight to the screening itself, not back to the MoSAIC splash — someone
// who has read this far has already chosen. Same tiles as the splash.
const QuizLinks = () => (
  <div className="border-t border-black pt-8 pb-4">
    <p className="text-black text-base mb-5">Ready to screen a relationship?</p>
    <ScreeningTiles />
  </div>
);

const MosaicPSYOPSExplainedPage = () => (
  <HubPageLayout>
    {SECTIONS.map((s, i) => (
      <Section key={s.title} title={s.title} body={s.body} first={i === 0} />
    ))}
    <QuizLinks />
  </HubPageLayout>
);

export default MosaicPSYOPSExplainedPage;
